import { ErrorMessage } from '@hookform/error-message';
import {
    FormControl,
    Popper,
    Autocomplete,
    AutocompleteProps,
    TextField,
} from '@mui/material';
import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { InputWithControllerProps } from '.';

interface OptionType {
    value: number;
    label: string;
}

type AutocompleteWithControllerProps = {
    autocompleteProps: Omit<
        AutocompleteProps<
            OptionType,
            boolean | undefined,
            boolean | undefined,
            boolean | undefined
        >,
        'renderInput'
    >;
    label?: string;
    handleInputChange?: (
        value: any,
        onChangeCallback: (...args) => void
    ) => void;
};

const AutocompleteWithController = ({
    controllerProps,
    autocompleteProps,
    handleChange,
    handleInputChange,
    errorAsPopper,
    label,
}: AutocompleteWithControllerProps & InputWithControllerProps) => {
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const { options } = autocompleteProps;
    return (
        <Controller
            {...controllerProps}
            render={({ field: { value, onChange } }) => {
                return (
                    <FormControl fullWidth size="small" ref={anchorRef}>
                        <Autocomplete
                            {...autocompleteProps}
                            size="small"
                            value={value ?? ''}
                            inputValue={options[value]?.label ?? ''}
                            onChange={(e, option) => {
                                if (handleChange) {
                                    handleChange(option?.['value'], onChange);
                                } else {
                                    onChange(option?.['value']);
                                }
                            }}
                            onInputChange={(e, value) => {
                                if (handleInputChange) {
                                    handleInputChange(value, onChange);
                                } else {
                                    onChange(value);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={label}
                                    variant="outlined"
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props}>{option?.label}</li>
                            )}
                        />
                        <ErrorMessage
                            name={controllerProps.name}
                            render={({ message }) =>
                                errorAsPopper ? (
                                    <Popper
                                        open={Boolean(message)}
                                        anchorEl={anchorRef.current}
                                        placement="bottom-start"
                                        disablePortal
                                    >
                                        <span className="p-1 text-danger">
                                            {message}
                                        </span>
                                    </Popper>
                                ) : (
                                    <span className="p-1 text-danger">
                                        {message}
                                    </span>
                                )
                            }
                        />
                    </FormControl>
                );
            }}
        />
    );
};

export default AutocompleteWithController;
